import React, { useMemo, useEffect } from 'react';
import { ProfileNavigation } from './ProfileNavigation';
import { Footer } from '../Footer';
import { Target, ChevronDown, ChevronUp } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ScoreRing } from '../ui/ScoreRing';

export function ProfilePage() {
  const location = useLocation();
  const { profile } = useAuth();
  const [showMobileNav, setShowMobileNav] = useState(false);
  const currentSection = location.hash.slice(1) || 'company';

  // Counts across the relational evidence tables (personnel, past performance, pricing,
  // compliance) so "Profile strength" reflects what actually drives proposals — not just
  // company-identity fields. Cheap head/count queries, owner-scoped via RLS.
  const [counts, setCounts] = useState({ pastPerformance: 0, personnel: 0, pricing: 0, compliance: 0 });

  useEffect(() => {
    const pid = (profile as any)?.id;
    if (!pid) return;
    let cancelled = false;
    (async () => {
      const head = (table: string) =>
        supabase.from(table).select('id', { count: 'exact', head: true }).eq('profile_id', pid);
      try {
        const [pp, kp, lab, comp] = await Promise.all([
          head('past_performance'), head('key_personnel'), head('company_labor_categories'), head('compliance_documents'),
        ]);
        if (cancelled) return;
        setCounts({
          pastPerformance: pp.count || 0,
          personnel: kp.count || 0,
          pricing: lab.count || 0,
          compliance: comp.count || 0,
        });
      } catch (e) {
        console.warn('Profile strength: could not load evidence counts', e);
      }
    })();
    return () => { cancelled = true; };
  }, [profile]);

  // Profile strength across the FULL evidence base that drives proposals (identity,
  // contact, classification, capabilities, past performance, personnel, pricing,
  // compliance) — so it can't read "Complete" while proposal-critical sections are empty.
  const { strength, gaps } = useMemo(() => {
    const p: any = profile || {};
    const dims: boolean[] = [
      !!p.company_name && (!!p.uei_number || !!p.cage_code) && !!p.business_address,                       // identity
      !!p.phone && !!p.email,                                                                              // contact
      Array.isArray(p.naics_codes) && p.naics_codes.length > 0,                                            // classification
      (Array.isArray(p.core_competencies) && p.core_competencies.length > 0) || !!p.value_proposition,     // capabilities
      counts.pastPerformance > 0,                                                                          // past performance
      counts.personnel > 0,                                                                                // key personnel
      counts.pricing > 0,                                                                                  // pricing (Costing Studio)
      (Array.isArray(p.certifications) && p.certifications.length > 0) || counts.compliance > 0,            // compliance
    ];
    const filled = dims.filter(Boolean).length;
    return {
      strength: Math.round((filled / dims.length) * 100),
      gaps: dims.length - filled,
    };
  }, [profile, counts]);

  // Get section title for mobile header
  const getSectionTitle = () => {
    switch(currentSection) {
      case 'company': return 'Company Information';
      case 'capabilities': return 'Capabilities';
      case 'personnel': return 'Key Personnel';
      case 'performance': return 'Past Performance';
      case 'pricing': return 'Pricing & Costing';
      case 'compliance': return 'Compliance';
      case 'templates': return 'Proposal Templates';
      case 'primes': return 'Preferred Primes';
      default: return 'Company Information';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-bw-app">
      {/* Header Section — the evidence base */}
      <div className="bg-bw-surface border-b border-bw-border mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-bw-ink tracking-tight">Company Profile</h1>
                <p className="text-sm text-bw-muted mt-0.5">The evidence base for every score and proposal</p>
              </div>
              {/* Profile strength */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <ScoreRing score={strength} size={44} color="rgb(var(--bw-primary))" aria-label={`Profile strength ${strength}%`} />
                <div className="hidden sm:block">
                  <div className="text-[12px] font-bold text-bw-ink">Profile strength</div>
                  <div className="text-[11px] text-bw-muted">
                    {gaps === 0 ? 'Complete' : `${gaps} gap${gaps === 1 ? '' : 's'} to close`}
                  </div>
                </div>
              </div>
            </div>

            {/* Grounding callout — what everything here grounds */}
            <div className="flex items-start gap-2.5 px-3.5 py-3 bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
              <Target className="h-4 w-4 text-bw-primary flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-bw-ink leading-relaxed">
                <strong className="font-semibold">Everything here grounds your scores and proposals.</strong>{' '}
                If a capability isn't listed, BidWin won't claim it — a stronger profile yields higher-confidence scores.
              </p>
            </div>

            {/* Mobile Section Navigation */}
            <div className="md:hidden">
              <button
                onClick={() => setShowMobileNav(!showMobileNav)}
                className="w-full flex items-center justify-between px-4 py-3 bg-bw-surface-subtle border border-bw-border rounded-lg text-bw-ink transition-colors hover:bg-bw-border/40"
              >
                <span className="font-medium">{getSectionTitle()}</span>
                {showMobileNav ? (
                  <ChevronUp className="h-5 w-5 text-bw-muted" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-bw-muted" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-8 overflow-hidden">
        <ProfileNavigation
          showMobileNav={showMobileNav}
          onCloseMobileNav={() => setShowMobileNav(false)}
        />
      </div>
      <Footer />
    </div>
  );
}
